import { markColor } from "../../lib/group-color.ts";
import { BandArt, bandArtFor, swimBandBackground, TEXT_ROOM } from "./band-art.tsx";
import {
  clientFlagWords,
  formatAED,
  INTRO_TO_PACK_DAYS,
  nudgeMessage,
  searchByName,
  type ManualPaymentMethod,
  type OwnerHome,
  type OwnerHomeAdult,
  type OwnerHomeLeft,
  type OwnerHomePerson,
  type OwnerHomeRunningOut,
  type OwnerHomeSession,
  type OwnerHomeStudioClient,
  type OwnerHomeWaiting,
  type RenewalReason,
  type OwnerNudgeKind,
  type SendNudgesResponse,
  type Sport,
  type BookingFor,
} from "@teamna/shared";
import { Fragment, useState, type CSSProperties, type ReactNode } from "react";
import { track } from "@teamna/web-client/analytics/web.ts";
import {
  Avatar,
  Button,
  CalendarIcon,
  ChipRow,
  ChevronDownIcon,
  ClockIcon,
  Field,
  PeopleIcon,
  PulseIcon,
  SearchField,
  SegmentedControl,
  TicketIcon,
  showToast,
} from "../../components/index.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { WhatsAppButton } from "../families/bits.tsx";
import { capital, childSearchTexts, clockText, digitsMatch, firstName, householdWord, phoneText, staffMessage, type Words } from "../families/model.ts";
import {
  capitalFirst,
  greeting,
  heroSentence,
  lastCameText,
  overdueTotal,
  owedTotal,
  owingFamilies,
  parseAmount,
  paymentDays,
  participantTotal,
  paymentSummary,
  quietToAsk,
  shortDate,
  tiles as buildTiles,
  type TileKey,
} from "./model.ts";

/**
 * The owner Home (2026-10-04): five numbers and, under each, the people behind it.
 *
 * The numbers sit on tiles of the same height, one colour each. A tap opens the people right under
 * that tile (on a phone, right under the box; on a wide screen, under its row), grouped the way the
 * owner thinks: by group, by day, by sessions left. Each person opens in place: the facts, the
 * WhatsApp button for each parent, one for the group's staff member, and the one thing to do.
 *
 * Pure: it draws an `OwnerHome` and reports taps, so Storybook shows every state.
 */

export type Hue = { main: string; ink: string; tint: string; soft: string };

const FAMILY_HUES = {
  participants: { main: "var(--color-accent)", ink: "var(--color-accent-strong)", tint: "var(--color-tile-swim-tint)", soft: "var(--color-tile-swim-soft)" },
  came: { main: "var(--color-tile-came)", ink: "var(--color-tile-came-ink)", tint: "var(--color-tile-came-tint)", soft: "var(--color-tile-came-soft)" },
  booked: { main: "var(--color-tile-booked)", ink: "var(--color-tile-booked-ink)", tint: "var(--color-tile-booked-tint)", soft: "var(--color-tile-booked-soft)" },
  quiet: { main: "var(--color-tile-quiet)", ink: "var(--color-tile-quiet-ink)", tint: "var(--color-tile-quiet-tint)", soft: "var(--color-tile-quiet-soft)" },
  run: { main: "var(--color-tile-run)", ink: "var(--color-tile-run-ink)", tint: "var(--color-tile-run-tint)", soft: "var(--color-tile-run-soft)" },
} satisfies Record<string, Hue>;

/** The studio numbers (H4) take the colours of the family tile that is nearest in meaning. */
export const HUES: Record<TileKey, Hue> = {
  ...FAMILY_HUES,
  fill: FAMILY_HUES.booked,
  active: FAMILY_HUES.participants,
  ending: FAMILY_HUES.run,
  intro: FAMILY_HUES.came,
  late: FAMILY_HUES.quiet,
};

const ICONS: Record<TileKey, ReactNode> = {
  participants: <PeopleIcon size={18} />,
  came: <PulseIcon size={18} />,
  booked: <CalendarIcon size={18} />,
  quiet: <ClockIcon size={18} />,
  run: <TicketIcon size={18} />,
  fill: <CalendarIcon size={18} />,
  active: <PeopleIcon size={18} />,
  ending: <TicketIcon size={18} />,
  intro: <PulseIcon size={18} />,
  late: <ClockIcon size={18} />,
};

/** The wide tiles: the second row of a business's five, under the three narrow ones. */
const WIDE: ReadonlySet<TileKey> = new Set(["quiet", "run", "intro", "late"]);

export type RecordPaymentInput = {
  participantId: string;
  amountFils: number;
  method: ManualPaymentMethod;
  paidOn: string;
} & ({ purchaseId: string; productId?: undefined } | { productId: string; purchaseId?: undefined });

export type HomeViewProps = {
  home: OwnerHome;
  words: Words;
  timezone: string;
  businessName: string;
  firstName: string | null;
  now: Date;
  /**
   * Sends the messages of Home in the Teamna app. Missing: no in-app send (the front desk, when the
   * owner did not turn on "Message many clients"); a row then offers WhatsApp only.
   */
  onSend?: ((items: Array<{ participantId: string; kind: OwnerNudgeKind }>) => Promise<SendNudgesResponse>) | undefined;
  onRecordPayment: (input: RecordPaymentInput) => Promise<void>;
  /** Open one panel at first: Storybook and the tests. */
  initialOpen?: TileKey | null;
  /** Under the greeting, above the numbers: the setup checklist while the business is new. */
  top?: ReactNode;
  /**
   * The main colour of the business logo (decision D7, 2026-10-14). The greeting band uses it, made
   * dark enough for white text to pass WCAG AA. Buttons stay Teamna blue. Null: Teamna blue.
   */
  brandColor?: string | null;
  /**
   * The business sport: each sport has its own drawing on the greeting band (`band-art.tsx`). Null
   * keeps the white wave.
   */
  sport?: Sport | null;
  /** The notices and the queues, under the numbers. */
  children?: ReactNode;
  /** A coach's photo, for the face next to "Coach <name>". Missing: the initials. */
  staffPhotoOf?: (userId: string) => string | null;
};

/**
 * The greeting band: Teamna blue, or the club colour when the logo has one. Both ends of the club
 * gradient are made dark enough for white text (`markColor`, AA 4.5:1), so the words on the band stay
 * readable for any logo.
 */
export function heroBackground(brandColor: string | null): string {
  if (!brandColor || !/^#[0-9a-fA-F]{6}$/.test(brandColor)) {
    // The last stop was #3f86d9 (3.7:1 for white text); #3672b8 is the same blue made AA-safe by
    // `markColor` (4.9:1), so every stop passes (review F3.1).
    return "linear-gradient(135deg, #1c449d 0%, #326cc5 60%, #3672b8 100%)";
  }
  const end = markColor(brandColor);
  const start = markColor(shade(end, 0.72));
  return `linear-gradient(135deg, ${start} 0%, ${end} 100%)`;
}

function shade(hex: string, keep: number): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const parts = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((part) => Math.round(part * keep));
  return `#${parts.map((part) => part.toString(16).padStart(2, "0")).join("")}`;
}

/** The shape as the nudge text needs it (F2). */
function bookingFor(words: Words): BookingFor {
  return words.forChildren ? "children" : "self";
}

/** Who pays, when the row has no adult with a name. */
function noName(words: Words): string {
  return words.forChildren ? "the family" : `the ${words.member}`;
}

/**
 * The line under a name on the quiet and running out lists: the group and the member who pays.
 * Where clients book for themselves the person is the member (F2), so the line has the group only,
 * and no "No class" for a client who picks each class.
 */
function personSub(groupName: string | null, memberName: string | undefined, words: Words): string | undefined {
  if (!words.forChildren) return groupName ?? undefined;
  return `${groupName ?? `No ${words.group}`}. ${capital(words.member)}: ${memberName ?? "No name"}`;
}

/**
 * True on a business with nothing to count yet: no participant, no session this week (of any kind,
 * private ones too) and nobody came in the last two weeks. Home then shows one line instead of tiles
 * of zeros. An older API build sends no `allSessions`: then we do not know, and the tiles stay
 * (review F2.1).
 */
export function isFirstDay(home: OwnerHome): boolean {
  return (
    participantTotal(home) === 0 &&
    home.booked.allSessions === 0 &&
    home.today.sessions === 0 &&
    home.came.lastWeek === 0 &&
    home.came.weekBefore === 0
  );
}

export function HomeView(props: HomeViewProps) {
  const { home, words, timezone, now } = props;
  const [open, setOpen] = useState<TileKey | null>(props.initialOpen ?? null);
  const list = buildTiles(home, words);
  const todayWord = home.today.sessions === 1 ? "session" : "sessions";
  const art = bandArtFor(props.sport);
  const brandColor = props.brandColor ?? null;
  const bandBackground = art === "swimming" ? swimBandBackground(brandColor) : heroBackground(brandColor);
  // On a band of 768px and wider the text stops before the drawing. Narrower, the drawing stays dark
  // enough behind it. The band breaks a long word anywhere (`overflow-wrap`, inherited by every row),
  // so a name with no space cannot run past the room into the drawing (review F2.1).
  const textRoom = art ? TEXT_ROOM[art] : "";
  const staffWord = home.today.staffOn === 1 ? words.staff : `${words.staff === "coach" ? "coaches" : `${words.staff}s`}`;

  return (
    <div className="flex flex-col gap-6">
      <section
        aria-label="Today"
        className="@container relative isolate overflow-hidden rounded-[20px] px-5 py-6 text-inverse shadow-card [overflow-wrap:anywhere] sm:px-7 sm:py-7"
        style={{ background: bandBackground }}
      >
        {art ? (
          <BandArt sport={art} brandColor={brandColor} />
        ) : (
          <svg aria-hidden="true" viewBox="0 0 1200 120" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 -z-10 h-16 w-full opacity-25">
            <path d="M0 60 C 200 10, 400 110, 600 60 S 1000 10, 1200 60 L 1200 120 L 0 120 Z" fill="#ffffff" />
            <path d="M0 85 C 250 40, 450 120, 700 80 S 1050 40, 1200 85 L 1200 120 L 0 120 Z" fill="#ffffff" opacity="0.6" />
          </svg>
        )}
        {/* Full white, no opacity: the band colours are made for AA against pure white (review F2.1). */}
        <p className={`text-[13px] font-semibold tracking-[0.06em] uppercase ${textRoom}`}>{props.businessName}</p>
        <h1 className={`mt-1 text-[26px] leading-tight sm:text-[30px] ${textRoom}`}>
          {greeting(now, timezone)}
          {props.firstName ? `, ${props.firstName}` : ""}
        </h1>
        <p className={`mt-2 max-w-[720px] text-[15px] leading-relaxed ${textRoom}`}>{heroSentence(home, words)}</p>
        <div className={`mt-4 flex flex-wrap gap-2 ${textRoom}`}>
          <span className="rounded-pill bg-white/15 px-3 py-1 text-[13px] font-semibold ring-1 ring-white/25">
            {home.today.sessions} {todayWord} today
          </span>
          <span className="rounded-pill bg-white/15 px-3 py-1 text-[13px] font-semibold ring-1 ring-white/25">
            {home.today.staffOn} {staffWord} on
          </span>
        </div>
      </section>

      {props.top}

      {isFirstDay(home) ? (
        // Day one: no tiles of zeros (strategy repo onboarding-v2.md, 2026-10-14). The numbers come
        // with the first participant or the first session.
        <section
          aria-label="Your numbers"
          className="flex items-start gap-3 rounded-card border border-dashed border-line-strong bg-paper px-5 py-4 text-ink-2"
        >
          <span className="mt-0.5 text-ink-3">
            <PulseIcon size={18} />
          </span>
          <p className="text-[15px]">
            Your numbers show here when you add your first {words.participant} or your first session: who came, who
            booked, who is quiet, and whose sessions run out.
          </p>
        </section>
      ) : (
      <div className="grid grid-cols-1 gap-4 [grid-auto-flow:row_dense] md:grid-cols-6">
        {list.map((tile) => {
          const hue = HUES[tile.key];
          const isOpen = open === tile.key;
          const dim = open !== null && !isOpen;
          return (
            <Fragment key={tile.key}>
              <button
                type="button"
                onClick={() => {
                  if (!isOpen) track("home_card_used", { card: "tile", action: "open_panel", tile: tile.key });
                  setOpen(isOpen ? null : tile.key);
                }}
                aria-expanded={isOpen}
                aria-controls={`home-panel-${tile.key}`}
                className={[
                  "group flex min-h-[188px] flex-col rounded-[18px] bg-paper p-4 text-left ring-1 transition-[box-shadow,opacity,filter,transform] duration-300",
                  WIDE.has(tile.key) ? "md:col-span-3" : "md:col-span-2",
                  isOpen ? "shadow-[0_12px_28px_rgba(17,26,43,.12)]" : "shadow-card hover:-translate-y-0.5",
                  dim ? "opacity-60 saturate-[.7]" : "",
                ].join(" ")}
                style={{ "--tw-ring-color": isOpen ? hue.main : "var(--color-line)", borderTop: `4px solid ${hue.main}` } as CSSProperties}
              >
                <span className="flex items-center gap-2">
                  <span className="flex size-8 items-center justify-center rounded-lg" style={{ background: hue.tint, color: hue.ink }}>
                    {ICONS[tile.key]}
                  </span>
                  <span className="min-w-0 flex-1 text-[14px] font-bold text-ink">{tile.label}</span>
                  <ChevronDownIcon size={18} className={`text-ink-3 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </span>
                <span className="mt-3 flex items-baseline gap-2">
                  <span className="text-[40px] leading-none font-bold tabular-nums" style={{ color: hue.ink }}>
                    {tile.value}
                  </span>
                  <span className="text-[14px] text-ink-2">{tile.unit}</span>
                </span>
                <span
                  className="mt-1.5 text-[13px] font-semibold"
                  style={{ color: tile.tone === "up" ? "var(--color-confirmed-ink)" : tile.tone === "down" ? "var(--color-tile-run-ink)" : "var(--color-ink-2)" }}
                >
                  {tile.delta}
                </span>
                <span className="mt-auto pt-3">
                  <TileVisual tileKey={tile.key} home={home} hue={hue} />
                </span>
              </button>
              <div id={`home-panel-${tile.key}`} className={isOpen ? "md:col-span-6" : "hidden"}>
                {isOpen ? <Panel tileKey={tile.key} hue={hue} {...props} /> : null}
              </div>
            </Fragment>
          );
        })}
      </div>
      )}
      {props.children}
    </div>
  );
}

function TileVisual({ tileKey, home, hue }: { tileKey: TileKey; home: OwnerHome; hue: Hue }) {
  if (tileKey === "participants") {
    return (
      <span className="flex flex-wrap gap-1.5">
        <span className="rounded-pill bg-confirmed-surface px-2.5 py-1 text-[12.5px] font-bold text-confirmed-ink">
          {home.participants.joinedThisMonth.length} joined
        </span>
        <span className="rounded-pill px-2.5 py-1 text-[12.5px] font-bold" style={{ background: "var(--color-tile-run-tint)", color: "var(--color-tile-run-ink)" }}>
          {home.participants.leftThisMonth.length} left
        </span>
      </span>
    );
  }
  if (tileKey === "came") {
    const max = Math.max(1, ...home.came.weeks.map((week) => week.came));
    return (
      <span className="flex h-10 items-end gap-1.5" aria-hidden="true">
        {home.came.weeks.map((week, index) => (
          <span
            key={week.weekStart}
            title={`${week.weekStart}: ${week.came}`}
            className="flex-1 rounded-t-md"
            style={{ height: `${Math.max(8, Math.round((week.came / max) * 100))}%`, background: index === home.came.weeks.length - 1 ? hue.main : hue.soft }}
          />
        ))}
      </span>
    );
  }
  if (tileKey === "booked" || tileKey === "fill") {
    const seats = tileKey === "fill" ? (home.studio?.fill ?? { booked: 0, seats: 0 }) : home.booked;
    const pct = seats.seats ? Math.round((seats.booked / seats.seats) * 100) : 0;
    return (
      <span className="block" aria-hidden="true">
        <span className="block h-2.5 overflow-hidden rounded-pill" style={{ background: hue.soft }}>
          <span className="block h-full rounded-pill" style={{ width: `${pct}%`, background: hue.main }} />
        </span>
        <span className="mt-1 block text-[12px] text-ink-2">{pct}% full</span>
      </span>
    );
  }
  if (tileKey === "quiet") {
    const ask = quietToAsk(home);
    return (
      <span className="flex items-center" aria-hidden="true">
        {ask.slice(0, 5).map((row, index) => (
          <span
            key={row.person.participantId}
            className="-ml-1.5 flex rounded-full ring-2 ring-paper first:ml-0"
            style={{ zIndex: 5 - index }}
          >
            <Avatar
              name={row.person.name}
              photoUrl={row.person.photoUrl ?? null}
              size={32}
              colors={{ background: hue.tint, color: hue.ink }}
            />
          </span>
        ))}
        {ask.length > 5 ? <span className="ml-2 text-[12.5px] font-semibold text-ink-2">+{ask.length - 5} more</span> : null}
      </span>
    );
  }
  if (tileKey === "active" || tileKey === "ending" || tileKey === "intro" || tileKey === "late") {
    return <StudioTileVisual tileKey={tileKey} home={home} hue={hue} />;
  }
  const counts = [0, 1, 2].map((left) => home.runningOut.filter((row) => row.person.sessionsLeft === left).length);
  const total = Math.max(1, counts.reduce((sum, value) => sum + value, 0));
  const colors = [hue.main, "var(--color-tile-quiet)", hue.soft];
  return (
    <span className="block" aria-hidden="true">
      <span className="flex h-2.5 overflow-hidden rounded-pill bg-sunken">
        {(["none", "one", "two"] as const).map((key, index) => (
          <span key={key} style={{ width: `${(counts[index]! / total) * 100}%`, background: colors[index] }} />
        ))}
      </span>
      <span className="mt-1 block text-[12px] text-ink-2">
        {counts[0]} with none left, {counts[1]} with 1, {counts[2]} with 2
      </span>
    </span>
  );
}

/** The small picture at the foot of a studio tile (H4). */
function StudioTileVisual({ tileKey, home, hue }: { tileKey: "active" | "ending" | "intro" | "late"; home: OwnerHome; hue: Hue }) {
  const studio = home.studio;
  if (!studio) return null;
  if (tileKey === "intro") {
    const total = studio.intro.clients.length;
    const converted = studio.intro.clients.filter((row) => row.packageName !== null).length;
    return (
      <span className="block" aria-hidden="true">
        <span className="block h-2.5 overflow-hidden rounded-pill" style={{ background: hue.soft }}>
          <span className="block h-full rounded-pill" style={{ width: `${total ? Math.round((converted / total) * 100) : 0}%`, background: hue.main }} />
        </span>
      </span>
    );
  }
  const faces =
    tileKey === "late"
      ? studio.lateCancels.map((row) => ({ id: row.bookingId, name: row.name }))
      : (tileKey === "active" ? studio.active : studio.endingSoon).map((row) => ({ id: row.familyId, name: row.name }));
  return (
    <span className="flex items-center" aria-hidden="true">
      {faces.slice(0, 5).map((face, index) => (
        <span key={face.id} className="-ml-1.5 flex rounded-full ring-2 ring-paper first:ml-0" style={{ zIndex: 5 - index }}>
          <Avatar name={face.name} photoUrl={null} size={32} colors={{ background: hue.tint, color: hue.ink }} />
        </span>
      ))}
      {faces.length > 5 ? <span className="ml-2 text-[12.5px] font-semibold text-ink-2">+{faces.length - 5} more</span> : null}
    </span>
  );
}

// --- panels -----------------------------------------------------------------

type RowAction = { label: string; variant: "primary" | "secondary"; onClick: () => void } | { label: string; whatsapp: { phone: string; text: string } };

export type Row = {
  id: string;
  /** The text in the circle when the row is not a person: "3/6" for a session. */
  avatar: string;
  avatarColor: string | null;
  /** A person's row: the circle shows their photo, or their initials. */
  face?: { name: string; photoUrl: string | null } | undefined;
  title: string;
  sub: string;
  badge?: { text: string; tone: "good" | "warn" | "bad" | "info" } | undefined;
  actions?: RowAction[];
  details?: { facts: Array<[string, string]>; adults: OwnerHomeAdult[]; childName: string; staff: OwnerHomePerson["staff"]; groupName: string | null } | undefined;
  pay?: { participantId: string; dueFils: number; dueText: string; target: { purchaseId: string } | { productId: string }; parentName: string } | undefined;
};

export type Group = { key: string; title: string; color: string | null; meter?: { value: number; total: number; text: string } | undefined; rows: Row[]; note?: string | undefined };

/** One message to send from Home, with the first name of who gets it (for the toast). */
export type NudgeItem = { participantId: string; kind: OwnerNudgeKind; parent: string };

/** Sends messages from Home and says in a toast what happened: sent, not in the app, or changed. */
export function useNudgeSend(onSend: HomeViewProps["onSend"]): { busy: boolean; send: ((items: NudgeItem[]) => Promise<void>) | null } {
  const [busy, setBusy] = useState(false);
  async function send(items: NudgeItem[]) {
    if (!onSend) return;
    setBusy(true);
    try {
      const result = await onSend(items.map(({ participantId, kind }) => ({ participantId, kind })));
      const sent = result.sent.length;
      const skipped = result.notJoined.length;
      const changed = result.changed.length;
      const reminders = result.sent.filter((row) => row.kind === "pay_reminder").length;
      const text =
        items.length === 1
          ? sent
            ? `Sent to ${items[0]!.parent} in the Teamna app.`
            : changed
              ? "Not sent: this changed since the page opened. The list is up to date now."
              : `${items[0]!.parent} is not in the app yet. Use WhatsApp.`
          : `Sent ${sent} ${sent === 1 ? "message" : "messages"}${reminders ? ` (${reminders} ${reminders === 1 ? "reminder" : "reminders"} to pay)` : ""} in the Teamna app.${skipped ? ` ${skipped} not in the app yet: use WhatsApp for them.` : ""}${changed ? ` ${changed} changed since the page opened: not sent.` : ""}`;
      showToast(text, { tone: sent ? "success" : "info" });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Not sent. Try again.", { tone: "info" });
    } finally {
      setBusy(false);
    }
  }
  return { busy, send: onSend ? send : null };
}

type RowContext = { words: Words; timezone: string; now: Date };

/** A person's row with the facts that open under it. */
export function personRow(row: OwnerHomePerson, { words, timezone, now }: RowContext, extra: Partial<Row> = {}): Row {
  return {
    id: row.participantId,
    avatar: "",
    avatarColor: row.groupColor,
    face: { name: row.name, photoUrl: row.photoUrl ?? null },
    title: row.name,
    sub: `${row.groupName ?? `No ${words.group}`}. ${capital(words.member)}: ${row.adults.map((adult) => adult.name).join(" and ")}`,
    details: {
      facts: [
        [capital(words.group), row.groupName ? `${row.groupName}${row.staffName ? `, ${capital(words.staff)} ${firstName(row.staffName)}` : ""}` : `No ${words.group} yet`],
        ["Age", row.age !== null ? String(row.age) : "Not given"],
        ["Sessions left", String(row.sessionsLeft)],
        ["Last came", lastCameText(row, now)],
        ["Came, last 4 weeks", row.booked4Weeks ? `${row.came4Weeks} of ${row.booked4Weeks}` : "No bookings"],
        ["Joined", shortDate(row.joinedAt, timezone)],
      ],
      adults: row.adults,
      childName: row.name,
      staff: row.staff,
      groupName: row.groupName,
    },
    ...extra,
  };
}

/** The kind of message a money row gets: a reminder when money is due, else a renewal. */
export function moneyKind(item: Pick<OwnerHomeRunningOut, "owed">): OwnerNudgeKind {
  return item.owed ? "pay_reminder" : "renewal";
}

/**
 * A row of Running out, or of Renewals (H3) with its reason: the badge, "Send renewal" or "Remind
 * to pay" (in Teamna, or on WhatsApp for a member not in the app yet), and "Record payment".
 */
export function moneyRow(
  item: OwnerHomeRunningOut & { reason?: RenewalReason; endsText?: string },
  ctx: RowContext & { businessName: string; send: ((items: NudgeItem[]) => void) | null; sub?: string },
): Row {
  const { words, timezone } = ctx;
  const parent = item.person.adults[0];
  const kind = moneyKind(item);
  const text = nudgeMessage(kind, {
    childName: item.person.name,
    businessName: ctx.businessName,
    sessionsLeft: item.person.sessionsLeft,
    ...(item.owed ? { dueText: formatAED(item.owed.dueFils), packageName: item.owed.packageName } : {}),
    bookingFor: bookingFor(words),
    ...(item.reason ? { reason: item.reason } : {}),
    ...(item.endsText ? { endsText: item.endsText } : {}),
  });
  const actions: RowAction[] = [];
  if (!item.nudge && parent) {
    const label = item.owed ? "Remind to pay" : "Send renewal";
    const send = ctx.send;
    if (parent.joined && send) actions.push({ label, variant: "secondary", onClick: () => send([{ participantId: item.person.participantId, kind, parent: firstName(parent.name) }]) });
    else if (parent.phoneE164) actions.push({ label: `${label} on WhatsApp`, whatsapp: { phone: parent.phoneE164, text } });
  }
  const badge = item.owed
    ? item.owed.paidFils
      ? { text: `${formatAED(item.owed.dueFils)} still due. Paid ${formatAED(item.owed.paidFils)}`, tone: "warn" as const }
      : item.nudge
        ? { text: `Reminder sent ${shortDate(item.nudge.sentAt, timezone)}`, tone: "info" as const }
        : { text: `${formatAED(item.owed.dueFils)} overdue`, tone: "bad" as const }
    : item.nudge
      ? { text: `Renewal sent ${shortDate(item.nudge.sentAt, timezone)}`, tone: "good" as const }
      : undefined;
  const pay = item.owed
    ? {
        participantId: item.person.participantId,
        dueFils: item.owed.dueFils,
        dueText: `Overdue for ${firstName(item.person.name)}, ${item.owed.packageName}: ${formatAED(item.owed.dueFils)}${item.owed.paidFils ? `. Already received: ${formatAED(item.owed.paidFils)}` : ""}.`,
        target: { purchaseId: item.owed.purchaseId },
        parentName: parent?.name ?? noName(words),
      }
    : item.renewal
      ? {
          participantId: item.person.participantId,
          dueFils: item.renewal.priceFils,
          dueText: `Next package for ${firstName(item.person.name)}, ${item.renewal.productName} (${item.renewal.credits} sessions): ${formatAED(item.renewal.priceFils)}.`,
          target: { productId: item.renewal.productId },
          parentName: parent?.name ?? noName(words),
        }
      : undefined;
  const sub = ctx.sub ?? personSub(item.person.groupName, parent?.name, words);
  // Came with no sessions left: a later package or paid month for the class pays these (2026-10-29).
  const owedSessions = item.owedSessions ? `${item.owedSessions} owed ${item.owedSessions === 1 ? "session" : "sessions"}` : null;
  return personRow(item.person, ctx, {
    sub: owedSessions ? (sub ? `${sub}. ${owedSessions}` : owedSessions) : sub,
    badge,
    actions,
    pay,
  });
}

/**
 * Sessions by day, each with its seats: "Full" and "Under half full". The Booked this week panel
 * and the studio Fill this week panel (H4) read it.
 */
export function sessionDayGroups(sessions: readonly OwnerHomeSession[], { words, timezone }: { words: Words; timezone: string }): Group[] {
  const days = [...new Set(sessions.map((session) => shortDate(session.startsAt, timezone)))];
  return days.map((day) => {
    const rows = sessions.filter((session) => shortDate(session.startsAt, timezone) === day);
    const booked = rows.reduce((sum, session) => sum + session.booked, 0);
    // A session with no limit counts its bookings as its seats, the same as the server total.
    const seats = rows.reduce((sum, session) => sum + (session.capacity ?? session.booked), 0);
    return {
      key: day,
      title: day,
      color: null,
      meter: { value: booked, total: seats, text: `${booked} of ${seats} seats` },
      rows: rows.map((session) => {
        // No limit: never full or under half full, and the row says how many are booked.
        const capacity = session.capacity;
        const full = capacity !== null && session.booked >= capacity;
        const thin = capacity !== null && session.booked * 2 < capacity;
        const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone }).format(new Date(session.startsAt));
        const staffText = session.staffName ? `${capital(words.staff)} ${session.staffName}` : `No ${words.staff} yet`;
        return {
          id: session.id,
          avatar: capacity === null ? String(session.booked) : `${session.booked}/${capacity}`,
          avatarColor: session.groupColor,
          title: `${session.groupName}, ${clockText(time)}`,
          sub: capacity === null ? `${session.booked} booked, no limit. ${staffText}` : staffText,
          badge: full ? { text: "Full", tone: "good" as const } : thin ? { text: "Under half full", tone: "warn" as const } : undefined,
        };
      }),
    };
  });
}

/** The line under a client of a studio number (H4): the package, the sessions left and the end date. */
function studioClientSub(row: OwnerHomeStudioClient, timezone: string): string {
  if (row.sessionsLeft === null) return row.nextBookingAt ? `No current package. Next booking ${shortDate(row.nextBookingAt, timezone)}` : "No current package";
  const left = `${row.sessionsLeft} ${row.sessionsLeft === 1 ? "session" : "sessions"} left`;
  return `${row.packageName ?? "Package"}. ${left}${row.endsOn ? `. Ends ${shortDate(`${row.endsOn}T12:00:00.000Z`, "UTC")}` : ""}`;
}

/** The money rows still to message, of members in the app: "Send all". */
export function unsentMoneyRows<T extends OwnerHomeRunningOut>(rows: readonly T[]): T[] {
  return rows.filter((row) => !row.nudge && row.person.adults.some((adult) => adult.joined));
}

function Panel({ tileKey, hue, ...props }: HomeViewProps & { tileKey: TileKey; hue: Hue }) {
  const { home, words, timezone, now } = props;
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "joined" | "left">("all");
  const { busy, send } = useNudgeSend(props.onSend);

  const person = (row: OwnerHomePerson, extra: Partial<Row> = {}): Row => personRow(row, { words, timezone, now }, extra);

  // A name that waits for a parent: no family yet, so no details and no message to send.
  const waitingRow = (row: OwnerHomeWaiting, extra: Partial<Row> = {}): Row => ({
    id: row.id,
    avatar: "",
    avatarColor: row.groupColor,
    face: { name: row.name, photoUrl: null },
    title: row.name,
    sub: `${row.groupName}. No ${words.member} yet`,
    ...extra,
  });
  const waiting = home.participants.waiting ?? [];

  const byGroup = (people: OwnerHomePerson[], make: (row: OwnerHomePerson) => Row, meter?: (groupId: string | null) => Group["meter"]): Group[] => {
    const keys = [...new Set(people.map((row) => row.groupId ?? "none"))];
    return keys
      .map((key) => {
        const rows = people.filter((row) => (row.groupId ?? "none") === key);
        const first = rows[0];
        return {
          key,
          title: first?.groupName ?? `No ${words.group} yet`,
          color: first?.groupColor ?? null,
          meter: meter ? meter(key === "none" ? null : key) : undefined,
          rows: rows.map(make),
        };
      })
      .sort((a, b) => (a.key === "none" ? 1 : b.key === "none" ? -1 : a.title.localeCompare(b.title)));
  };

  // The swimmers tile: the name search rule over the child, the group and the adults, best first; or an adult's number.
  const homeSearch = <T extends { name: string; groupName: string | null; adults: OwnerHomeAdult[] }>(rows: T[]) =>
    searchByName(rows, query, (row) => childSearchTexts(row.name, row.groupName, row.adults), {
      extra: (row) => row.adults.some((adult) => digitsMatch(adult.phoneE164, query)),
    });
  const leftFound = tileKey === "participants" && filter === "left" ? homeSearch(home.participants.leftThisMonth) : null;
  const peopleFound =
    tileKey === "participants" && filter !== "left" ? homeSearch(filter === "joined" ? home.participants.joinedThisMonth : home.participants.all) : null;
  // The names that wait for a parent count in All, as on the Swimmers page (pilot, 2026-10-22),
  // found by the same name search rule over the name and the group.
  const waitingFound = tileKey === "participants" && filter === "all" ? searchByName(waiting, query, (row) => childSearchTexts(row.name, row.groupName, [])) : null;
  const homeFound = leftFound ?? peopleFound;
  // The event describes what the screen shows: the waiting names that matched are in it too.
  useTrackNameSearch(
    "home_participants",
    query,
    [...(homeFound?.results ?? []), ...(waitingFound?.results ?? [])],
    (homeFound?.rows.length ?? 0) + (waitingFound?.rows.length ?? 0),
  );

  let title = "";
  let lead = "";
  let groups: Group[] = [];
  let tools: ReactNode = null;
  let chart: ReactNode = null;

  if (tileKey === "participants") {
    if (leftFound) {
      const found = leftFound;
      const left = found.rows;
      groups = [{ key: "left", title: "Left this month", color: null, rows: left.map((row) => leftRow(row)) }];
      title = found.searched ? `${left.length} match` : `${left.length} left this month`;
    } else if (peopleFound) {
      const found = peopleFound;
      const shown = found.rows;
      const joinedIds = new Set(home.participants.joinedThisMonth.map((row) => row.participantId));
      groups = byGroup(shown, (row) => person(row, joinedIds.has(row.participantId) ? { badge: { text: `New, ${shortDate(row.joinedAt, timezone)}`, tone: "good" } } : {}));
      const waitingShown = waitingFound?.rows ?? [];
      if (waitingShown.length) {
        groups.push({
          key: "waiting",
          title: `No ${words.member} yet`,
          color: null,
          rows: waitingShown.map((row) => waitingRow(row, { badge: { text: `No ${words.member} yet`, tone: "info" } })),
          note: `Link them to a ${words.member} on the ${capital(words.participants)} page.`,
        });
      }
      const count = shown.length + waitingShown.length;
      title = found.searched
        ? `${count} ${count === 1 ? `${words.participant} matches` : `${words.participants} match`}`
        : filter === "joined"
          ? `${count} joined this month`
          : `${count} ${words.participants} in ${home.participants.groups} ${home.participants.groups === 1 ? words.group : words.groups}`;
    }
    lead = `Tap a name to see everything about the ${words.participant} and to message the ${words.member} or the ${words.staff}.`;
    tools = (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField label={`Search ${words.participants}`} value={query} onChange={setQuery} placeholder={`A ${words.participant}, a ${words.member}, a ${words.group} or a number`} className="flex-1" />
        <SegmentedControl
          label="Show"
          value={filter}
          onChange={(value) => setFilter(value as typeof filter)}
          options={[
            { value: "all", label: `All ${participantTotal(home)}` },
            { value: "joined", label: `Joined ${home.participants.joinedThisMonth.length}` },
            { value: "left", label: `Left ${home.participants.leftThisMonth.length}` },
          ]}
        />
      </div>
    );
  }

  function leftRow(row: OwnerHomeLeft): Row {
    return {
      id: row.participantId,
      avatar: "",
      avatarColor: null,
      face: { name: row.name, photoUrl: row.photoUrl ?? null },
      title: row.name,
      sub: row.groupName ?? `No ${words.group}`,
      badge: { text: `Left ${shortDate(row.leftAt, timezone)}`, tone: "bad" },
      details: { facts: [[capital(words.group), row.groupName ?? "None"], ["Left on", shortDate(row.leftAt, timezone)]], adults: row.adults, childName: row.name, staff: row.staff, groupName: row.groupName },
    };
  }

  if (tileKey === "came") {
    const total = participantTotal(home);
    title = `${home.came.lastWeek} of ${total} ${total === 1 ? words.participant : words.participants} came last week`;
    lead = `Counted from bookings in sessions that took place, and from the names with no ${words.member} yet that the ${words.staff} saved as came. A ${words.participant} the ${words.staff} marked absent does not count. Open a ${words.group} to see who did not come.`;
    const max = Math.max(1, ...home.came.weeks.map((week) => week.came));
    chart = (
      <div className="flex h-36 items-end gap-3 rounded-xl px-3 pt-4 pb-2" style={{ background: hue.tint }}>
        {home.came.weeks.map((week, index) => (
          <div key={week.weekStart} className="flex flex-1 flex-col items-center gap-1.5">
            <span className="text-[12px] font-bold tabular-nums" style={{ color: hue.ink }}>
              {week.came}
            </span>
            <span
              className="w-full max-w-[56px] rounded-t-lg"
              style={{ height: `${Math.max(6, Math.round((week.came / max) * 84))}px`, background: index === home.came.weeks.length - 1 ? hue.main : hue.soft }}
            />
            <span className="text-[11.5px] text-ink-2">{index === home.came.weeks.length - 1 ? "Last week" : shortDate(`${week.weekStart}T12:00:00.000Z`, "UTC").replace(/^\w+ /, "")}</span>
          </div>
        ))}
      </div>
    );
    groups = home.came.byGroup.map((row) => {
      const absent = home.came.absent.filter((entry) => entry.groupId === row.groupId);
      const waitingAbsent = waiting.filter((entry) => entry.groupId === row.groupId && !entry.cameLastWeek);
      return {
        key: row.groupId,
        title: row.groupName,
        color: row.groupColor,
        meter: { value: row.came, total: row.total, text: `${row.came} of ${row.total} came` },
        rows: [
          ...absent.map((item) => person(item, { badge: { text: "Did not come", tone: "warn" }, sub: `${item.groupName}. Last came ${lastCameText(item, now).toLowerCase()}` })),
          ...waitingAbsent.map((item) =>
            waitingRow(item, { badge: { text: "Did not come", tone: "warn" }, sub: `${item.groupName}. No ${words.member} yet. Last came ${lastCameText(item, now).toLowerCase()}` }),
          ),
        ],
        note: absent.length + waitingAbsent.length ? `${absent.length + waitingAbsent.length} did not come. Tap a name for details.` : "Everyone came.",
      };
    });
  }

  if (tileKey === "booked") {
    title = `${home.booked.booked} of ${home.booked.seats} seats booked this week`;
    lead = `${home.booked.seats - home.booked.booked} seats are still empty.${words.forChildren ? ` ${capital(words.members)} book only their child's ${words.group}.` : ""}`;
    groups = sessionDayGroups(home.booked.sessions, { words, timezone });
  }

  if (tileKey === "quiet") {
    const ask = quietToAsk(home);
    title = ask.length
      ? `${ask.length} ${ask.length === 1 ? `${words.participant} has` : `${words.participants} have`} not come in 2 weeks`
      : "Everyone has been asked";
    lead = `They came before, still have sessions left, and have nothing booked. A message takes them off this list for 10 days.`;
    groups = byGroup(
      home.quiet.map((row) => row.person),
      (row) => {
        const item = home.quiet.find((entry) => entry.person.participantId === row.participantId)!;
        const parent = row.adults[0];
        const text = nudgeMessage("quiet", { childName: row.name, businessName: props.businessName, sessionsLeft: row.sessionsLeft, bookingFor: bookingFor(words) });
        const actions: RowAction[] = [];
        if (!item.nudge && parent) {
          if (parent.joined && send) actions.push({ label: `Ask ${firstName(parent.name)} in Teamna`, variant: "secondary", onClick: () => void send([{ participantId: row.participantId, kind: "quiet", parent: firstName(parent.name) }]) });
          if (parent.phoneE164) actions.push({ label: `WhatsApp ${firstName(parent.name)}`, whatsapp: { phone: parent.phoneE164, text } });
        }
        return person(row, {
          badge: item.nudge ? { text: `Asked ${shortDate(item.nudge.sentAt, timezone)}`, tone: "good" } : { text: `${item.daysSince} days`, tone: "warn" },
          sub: personSub(row.groupName, parent?.name, words),
          actions,
        });
      },
    );
  }

  if (tileKey === "run") {
    const owed = owedTotal(home);
    title = home.runningOut.length
      ? `${home.runningOut.length} ${home.runningOut.length === 1 ? `${words.participant} is` : `${words.participants} are`} running out of sessions`
      : "Nobody is running out";
    // A family business counts families; a studio counts its clients (F2), with the same rule.
    const many = (count: number) => householdWord(count, words);
    lead = owed
      ? `${formatAED(owed)} is overdue from ${owingFamilies(home)} of these ${many(2)}: they get a reminder to pay. The others get a renewal.`
      : `Nothing is overdue from these ${many(2)}. Each one gets a renewal.`;
    // The tile shows everything overdue; the families here are only the ones running out.
    const all = overdueTotal(home);
    if (all.totalFils > owed) {
      lead += ` In all, ${formatAED(all.totalFils)} is overdue from ${all.families} ${many(all.families)}. Ask AI Assistant who owes to see them all.`;
    }
    const unsent = unsentMoneyRows(home.runningOut);
    if (unsent.length && send) {
      tools = (
        <div>
          <Button
            loading={busy}
            onClick={() => void send(unsent.map((row) => ({ participantId: row.person.participantId, kind: moneyKind(row), parent: firstName(row.person.adults[0]?.name ?? "") })))}
          >
            Send all {unsent.length} {unsent.length === 1 ? "message" : "messages"}
          </Button>
        </div>
      );
    }
    groups = [0, 1, 2]
      .map((left) => {
        const rows = home.runningOut.filter((row) => row.person.sessionsLeft === left);
        return {
          key: `left-${left}`,
          title: left === 0 ? "No sessions left" : left === 1 ? "1 session left" : "2 sessions left",
          color: left === 0 ? "var(--color-tile-run)" : left === 1 ? "var(--color-tile-quiet)" : "var(--color-accent)",
          rows: rows.map((item) => moneyRow(item, { words, timezone, now, businessName: props.businessName, send: send ? (items) => void send(items) : null })),
        };
      })
      .filter((group) => group.rows.length > 0);
  }

  if (home.studio && (tileKey === "fill" || tileKey === "active" || tileKey === "ending" || tileKey === "intro" || tileKey === "late")) {
    const panel = studioPanel(tileKey, home, { words, timezone, now });
    title = panel.title;
    lead = panel.lead;
    groups = panel.groups;
  }

  const empty = groups.every((group) => group.rows.length === 0) && tileKey !== "came" && tileKey !== "booked";

  return (
    <section
      aria-label={title}
      className="rounded-[18px] bg-paper p-4 shadow-[0_12px_28px_rgba(17,26,43,.10)] ring-1 motion-safe:animate-sheet sm:p-5"
      style={{ "--tw-ring-color": hue.soft } as CSSProperties}
    >
      <p className="text-[12px] font-bold tracking-[0.06em] uppercase" style={{ color: hue.ink }}>
        {buildTiles(home, words).find((tile) => tile.key === tileKey)?.label}
      </p>
      <h2 className="mt-1 text-[20px]">{title}</h2>
      <p className="mt-1 max-w-[760px] text-[14px] text-ink-2">{lead}</p>
      {tools ? <div className="mt-4">{tools}</div> : null}
      {chart ? <div className="mt-4">{chart}</div> : null}
      <div className="mt-4 flex flex-col gap-3">
        {empty ? (
          <p className="rounded-xl bg-surface px-4 py-6 text-center text-[14px] text-ink-2">
            {EMPTY_TEXT[tileKey]?.(words) ?? "Nothing matches."}
          </p>
        ) : (
          groups.map((group) => (
            <GroupBlock key={group.key} group={group} hue={hue} words={words} timezone={timezone} now={now} onRecordPayment={props.onRecordPayment} staffPhotoOf={props.staffPhotoOf} />
          ))
        )}
      </div>
    </section>
  );
}

/** What an empty panel says. */
const EMPTY_TEXT: Partial<Record<TileKey, (words: Words) => string>> = {
  run: () => "Nobody is running out. Good.",
  quiet: () => "Nobody to ask. Good.",
  fill: () => "No class this week.",
  active: (words) => `No active ${words.members} yet.`,
  ending: () => "No package ends soon. Good.",
  intro: () => `No intro offer in the last ${INTRO_TO_PACK_DAYS} days.`,
  late: () => "No late cancels this week. Good.",
};

/**
 * The panel of a studio number (H4): the people behind it. Active and Ending soon are the Clients
 * filters of C1; a row opens the facts when the client is on Home's list of participants.
 */
function studioPanel(
  tileKey: "fill" | "active" | "ending" | "intro" | "late",
  home: OwnerHome,
  ctx: RowContext,
): { title: string; lead: string; groups: Group[] } {
  const studio = home.studio!;
  const { words, timezone } = ctx;
  const people = new Map(home.participants.all.map((person) => [person.participantId, person]));
  const clientRow = (row: { familyId: string; participantId: string | null; name: string }, extra: Partial<Row>): Row => {
    const person = row.participantId ? people.get(row.participantId) : undefined;
    if (person) return personRow(person, ctx, extra);
    return { id: row.familyId, avatar: "", avatarColor: null, face: { name: row.name, photoUrl: null }, title: row.name, sub: "", ...extra };
  };
  const flagged = (row: OwnerHomeStudioClient): Partial<Row> => {
    const flags = clientFlagWords(row);
    return { sub: studioClientSub(row, timezone), ...(flags.length ? { badge: { text: flags.join(", "), tone: row.owed ? ("bad" as const) : ("info" as const) } } : {}) };
  };

  if (tileKey === "fill") {
    const { booked, seats } = studio.fill;
    return {
      title: seats ? `${Math.round((booked / seats) * 100)}% full this week` : "No class this week",
      lead: `${booked} of ${seats} places booked in the group classes, Monday to Sunday. Private sessions do not count.`,
      groups: sessionDayGroups(studio.fill.sessions, { words, timezone }),
    };
  }
  if (tileKey === "active" || tileKey === "ending") {
    const rows = tileKey === "active" ? [...studio.active].sort((a, b) => a.name.localeCompare(b.name)) : [...studio.endingSoon].sort((a, b) => (a.endsOn ?? "").localeCompare(b.endsOn ?? "") || a.name.localeCompare(b.name));
    return tileKey === "active"
      ? {
          title: `${rows.length} active ${rows.length === 1 ? words.member : words.members}`,
          lead: `A current package, or a next booking. The same rule as Active on the ${capital(words.members)} page.`,
          groups: [{ key: "active", title: "Active", color: null, rows: rows.map((row) => clientRow(row, flagged(row))) }],
        }
      : {
          title: `${rows.length} ${rows.length === 1 ? "package ends" : "packages end"} soon`,
          lead: `The package ends in 14 days or fewer, or has 2 sessions left or fewer. The same rule as Ending soon on the ${capital(words.members)} page. Send the renewals from the Renewals card.`,
          groups: [{ key: "ending", title: "Ending soon", color: null, rows: rows.map((row) => clientRow(row, flagged(row))) }],
        };
  }
  if (tileKey === "intro") {
    const bought = studio.intro.clients.filter((row) => row.packageName !== null);
    const notYet = studio.intro.clients.filter((row) => row.packageName === null);
    const total = studio.intro.clients.length;
    const introDay = (key: string) => shortDate(`${key}T12:00:00.000Z`, "UTC");
    return {
      title: total ? `${Math.round((bought.length / total) * 100)}% bought a package after the intro offer` : `No intro offer in the last ${studio.intro.windowDays} days`,
      lead: `Of the ${words.members} who bought an intro offer in the last ${studio.intro.windowDays} days, ${bought.length} of ${total} bought a full package after it.`,
      groups: [
        { key: "bought", title: "Bought a package", color: "var(--color-confirmed)", rows: bought.map((row) => clientRow(row, { sub: `Intro on ${introDay(row.introOn)}`, badge: { text: row.packageName!, tone: "good" } })) },
        { key: "not-yet", title: "No package yet", color: "var(--color-tile-quiet)", rows: notYet.map((row) => clientRow(row, { sub: `Intro on ${introDay(row.introOn)}`, badge: { text: "No package yet", tone: "warn" } })) },
      ].filter((group) => group.rows.length > 0),
    };
  }
  const time = (iso: string) => clockText(new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone }).format(new Date(iso)));
  return {
    title: `${studio.lateCancels.length} late ${studio.lateCancels.length === 1 ? "cancel" : "cancels"} this week`,
    lead: "Cancelled after the cut-off of your cancellation rule, Monday to Sunday. The rule says if the session is lost.",
    groups: studio.lateCancels.length
      ? [
          {
            key: "late",
            title: "Late cancels",
            color: null,
            rows: studio.lateCancels.map((row) =>
              clientRow(row, { id: row.bookingId, sub: `${row.groupName}, ${shortDate(row.startsAt, timezone)} ${time(row.startsAt)}`, badge: { text: `Cancelled ${shortDate(row.cancelledAt, timezone)}`, tone: "warn" } }),
            ),
          },
        ]
      : [],
  };
}

export function GroupBlock({
  group,
  hue,
  words,
  timezone,
  now,
  onRecordPayment,
  staffPhotoOf,
}: {
  group: Group;
  hue: Hue;
  words: Words;
  timezone: string;
  now: Date;
  onRecordPayment: HomeViewProps["onRecordPayment"];
  staffPhotoOf: HomeViewProps["staffPhotoOf"];
}) {
  const [open, setOpen] = useState(true);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [paying, setPaying] = useState<string | null>(null);
  return (
    <div className="overflow-hidden rounded-xl ring-1 ring-line">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex min-h-12 w-full items-center gap-3 bg-surface px-3.5 py-2 text-left"
      >
        <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full" style={{ background: group.color ?? hue.main }} />
        <span className="min-w-0 flex-1 font-bold text-ink">{group.title}</span>
        {group.meter ? (
          <span className="hidden w-32 sm:block" aria-hidden="true">
            <span className="block h-1.5 overflow-hidden rounded-pill" style={{ background: hue.soft }}>
              <span className="block h-full rounded-pill" style={{ width: `${Math.round((group.meter.value / Math.max(1, group.meter.total)) * 100)}%`, background: hue.main }} />
            </span>
          </span>
        ) : null}
        <span className="text-[13px] font-semibold text-ink-2">{group.meter ? group.meter.text : group.rows.length}</span>
        <ChevronDownIcon size={16} className={`text-ink-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <ul>
          {group.rows.map((row) => (
            <PersonRow
              key={row.id}
              row={row}
              hue={hue}
              words={words}
              timezone={timezone}
              now={now}
              open={openRow === row.id}
              onToggle={() => setOpenRow(openRow === row.id ? null : row.id)}
              paying={paying === row.id}
              onPay={() => setPaying(paying === row.id ? null : row.id)}
              onPaid={() => setPaying(null)}
              onRecordPayment={onRecordPayment}
              staffPhotoOf={staffPhotoOf}
            />
          ))}
          {group.note ? <li className="border-t border-line px-3.5 py-2 text-[13px] text-ink-2">{group.note}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

const BADGE: Record<NonNullable<Row["badge"]>["tone"], string> = {
  good: "bg-confirmed-surface text-confirmed-ink",
  warn: "bg-late-surface text-late-ink",
  bad: "bg-cancelled-surface text-cancelled-ink",
  info: "bg-accent-surface text-accent-strong",
};

function PersonRow({
  row,
  hue,
  words,
  timezone,
  now,
  open,
  onToggle,
  paying,
  onPay,
  onPaid,
  onRecordPayment,
  staffPhotoOf,
}: {
  row: Row;
  hue: Hue;
  words: Words;
  timezone: string;
  now: Date;
  open: boolean;
  onToggle: () => void;
  paying: boolean;
  onPay: () => void;
  onPaid: () => void;
  onRecordPayment: HomeViewProps["onRecordPayment"];
  staffPhotoOf: HomeViewProps["staffPhotoOf"];
}) {
  const expandable = Boolean(row.details);
  return (
    <li className="border-t border-line first:border-t-0">
      <div className="flex flex-col gap-2 px-3.5 py-2.5 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={expandable ? onToggle : undefined}
          aria-expanded={expandable ? open : undefined}
          disabled={!expandable}
          className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
        >
          {row.face ? (
            <Avatar
              name={row.face.name}
              photoUrl={row.face.photoUrl}
              size={36}
              colors={row.avatarColor ? { background: row.avatarColor, color: "#ffffff" } : { background: hue.tint, color: hue.ink }}
            />
          ) : (
            <span
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
              style={row.avatarColor ? { background: row.avatarColor, color: "#ffffff" } : { background: hue.tint, color: hue.ink }}
            >
              {row.avatar}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-ink" dir="auto">
              {row.title}
            </span>
            <span className="block truncate text-[13px] text-ink-2">{row.sub}</span>
          </span>
          {row.badge ? (
            <span className={`shrink-0 rounded-pill px-2.5 py-1 text-[12px] font-bold ${BADGE[row.badge.tone]}`}>{row.badge.text}</span>
          ) : null}
          {expandable ? <ChevronDownIcon size={16} className={`shrink-0 text-ink-3 transition-transform ${open ? "rotate-180" : ""}`} /> : null}
        </button>
        {row.actions?.length || row.pay ? (
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {row.actions?.map((action) =>
              "whatsapp" in action ? (
                <WhatsAppButton key={action.label} phoneE164={action.whatsapp.phone} text={action.whatsapp.text}>
                  {action.label}
                </WhatsAppButton>
              ) : (
                <Button key={action.label} variant={action.variant} onClick={action.onClick}>
                  {action.label}
                </Button>
              ),
            )}
            {row.pay ? (
              <Button variant="secondary" onClick={onPay} aria-expanded={paying}>
                Record payment
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {paying && row.pay ? <PaymentForm pay={row.pay} timezone={timezone} now={now} onDone={onPaid} onRecordPayment={onRecordPayment} /> : null}
      {open && row.details ? (
        <div className="flex flex-col gap-3 bg-surface px-3.5 pt-1 pb-3.5 motion-safe:animate-sheet">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
            {row.details.facts.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11.5px] font-semibold tracking-[0.02em] text-ink-2 uppercase">{label}</dt>
                <dd className="text-[14px] text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-2">
            {row.details.adults.map((adult) => (
              <span key={adult.userId} className="flex items-center gap-2 rounded-xl bg-paper py-1 pr-1 pl-3 ring-1 ring-line">
                <span className="text-[13px]">
                  <span className="font-semibold text-ink" dir="auto">
                    {adult.name}
                  </span>{" "}
                  <span className="text-ink-2">{phoneText(adult.phoneE164)}</span>
                </span>
                {adult.phoneE164 ? (
                  <WhatsAppButton phoneE164={adult.phoneE164} text={`Hi ${firstName(adult.name)}. About ${firstName(row.details!.childName)}: `}>
                    Message {firstName(adult.name)}
                  </WhatsAppButton>
                ) : null}
              </span>
            ))}
            {row.details.staff?.phoneE164 ? (
              <span className="flex items-center gap-2 rounded-xl bg-paper py-1 pr-1 pl-1.5 ring-1 ring-line">
                <Avatar name={row.details.staff.name} photoUrl={staffPhotoOf?.(row.details.staff.userId) ?? null} size={24} />
                <span className="text-[13px]">
                  <span className="font-semibold text-ink">
                    {capital(words.staff)} {row.details.staff.name}
                  </span>
                </span>
                <WhatsAppButton phoneE164={row.details.staff.phoneE164} text={staffMessage(row.details.childName, row.details.groupName)}>
                  Message the {words.staff}
                </WhatsAppButton>
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

function PaymentForm({
  pay,
  timezone,
  now,
  onDone,
  onRecordPayment,
}: {
  pay: NonNullable<Row["pay"]>;
  timezone: string;
  now: Date;
  onDone: () => void;
  onRecordPayment: HomeViewProps["onRecordPayment"];
}) {
  const days = paymentDays(now, timezone);
  const [amount, setAmount] = useState(String(pay.dueFils / 100));
  const [method, setMethod] = useState<ManualPaymentMethod | "">("");
  const [day, setDay] = useState(days[0]!.key);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fils = parseAmount(amount);
  const summary = paymentSummary(fils, pay.dueFils);

  async function save() {
    if (!fils) return setProblem("Type the amount first.");
    if (summary.tooMuch) return setProblem(summary.text);
    if (!method) return setProblem("Choose how they paid.");
    setSaving(true);
    setProblem(null);
    try {
      await onRecordPayment({ participantId: pay.participantId, amountFils: fils, method, paidOn: day, ...pay.target } as RecordPaymentInput);
      const dayLabel = days.find((row) => row.key === day)?.label.split(", ")[1] ?? "";
      showToast(
        `${formatAED(fils)} ${method === "cash" ? "cash" : "bank transfer"}, ${dayLabel}, from ${firstName(pay.parentName)} recorded. ${
          summary.full ? "Paid in full." : `${formatAED(pay.dueFils - fils)} is still due.`
        }`,
      );
      onDone();
    } catch (error) {
      setProblem(error instanceof Error && error.message ? error.message : "Not saved. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-3.5 mb-3 flex flex-col gap-3 rounded-xl bg-surface p-3.5 ring-1 ring-line motion-safe:animate-sheet">
      <div>
        <p className="font-semibold text-ink">Record a payment from {pay.parentName}</p>
        <p className="text-[13px] text-ink-2">{pay.dueText}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]">
        <Field size="compact" label="Amount received (AED)" inputMode="decimal" value={amount} onChange={(event) => { setAmount(event.target.value); setProblem(null); }} />
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-ink">How</span>
          <SegmentedControl
            label="How they paid"
            value={method}
            onChange={(value) => {
              setMethod(value as ManualPaymentMethod);
              setProblem(null);
            }}
            options={[
              { value: "cash", label: "Cash" },
              { value: "transfer", label: "Bank transfer" },
            ]}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-ink">When</span>
        <ChipRow
          label="When they paid"
          wrap
          selectedId={day}
          onSelect={(key) => {
            setDay(key);
            setProblem(null);
          }}
          options={days.map((option) => ({ id: option.key, label: option.label }))}
        />
      </div>
      <p className={`text-[13.5px] font-semibold ${summary.tooMuch ? "text-cancelled-ink" : summary.full ? "text-confirmed-ink" : "text-late-ink"}`} aria-live="polite">
        {problem ?? summary.text}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save()} loading={saving}>
          Save payment
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export { capitalFirst };
